import React from 'react';
import { useMutation, MutationObserverOptions } from '@tanstack/react-query';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel
} from '@epoch-ui/components/AlertDialog';
import { DeleteDraftEventResponse, draftEventApi } from '@event-editor/api/draft-event';

type DeleteDraftEventMutationOptions = MutationObserverOptions<
  DeleteDraftEventResponse['deleteDraftEvent'],
  Error,
  number,
  unknown
>;

interface DeleteDraftEventConfirmationModalProps extends React.ComponentProps<typeof AlertDialog> {
  eventId: number;
  onSuccess: DeleteDraftEventMutationOptions['onSuccess'];
  onError: DeleteDraftEventMutationOptions['onError'];
}

const DeleteDraftEventConfirmationModal: React.FC<DeleteDraftEventConfirmationModalProps> = ({
  eventId,
  onSuccess,
  onError,
  ...modalProps
}) => {
  const deleteDraftEventMutation = useMutation({
    mutationFn: draftEventApi.delete,
    onSuccess,
    onError
  });

  return (
    <AlertDialog {...modalProps}>
      <AlertDialogContent className="tw-w-[400px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete draft event?</AlertDialogTitle>
        </AlertDialogHeader>
        <p className="tw-m-0">
          Deleting this event will remove it from Epoch. You can’t undo this action.
        </p>
        <AlertDialogFooter>
          <AlertDialogCancel variant="secondary" disabled={deleteDraftEventMutation.isPending}>
            Keep draft
          </AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={deleteDraftEventMutation.isPending}
            onClick={() => deleteDraftEventMutation.mutate(eventId)}
          >
            Delete draft
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export { DeleteDraftEventConfirmationModal };
